import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Filter, Loader2, MapPin, Search, ShieldAlert, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Admin campsite verification queue. Backed by GET /admin/campsites with
// optional status + search. Owner can approve (POST /verify) or reject
// (POST /reject) each campsite. Sort order (from backend): pending_review
// first, then unverified, rejected, verified.
const STATUS_TABS = [
  { key: "pending_review", label: "Pending" },
  { key: "unverified",     label: "Unverified" },
  { key: "verified",       label: "Verified" },
  { key: "rejected",       label: "Rejected" },
  { key: "all",            label: "All" },
];

export default function AdminCampsites() {
  const { api } = useAuth();
  const [status, setStatus] = useState("pending_review");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/admin/campsites", {
        params: {
          status: status === "all" ? undefined : status,
          search: search.trim() || undefined,
        },
      });
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load campsites.");
    }
  }, [api, status, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const doAction = async (c, action) => {
    setBusyId(c.campsite_id);
    try {
      if (action === "verify") await api.post(`/admin/campsites/${c.campsite_id}/verify`);
      else if (action === "reject") await api.post(`/admin/campsites/${c.campsite_id}/reject`);
      toast.success(`${c.name} ${action === "verify" ? "verified" : "rejected"}.`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not update campsite.";
      toast.error(detail);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="admin-campsites-page">
      <div>
        <Link to="/admin" className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
              style={{ color: "var(--brand)" }} data-testid="admin-campsites-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Admin
        </Link>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-2" style={{ color: "var(--on-surface)" }}>
          Verification queue
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
          Review submissions from owners. Verifying a campsite makes it visible in Explore.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, state or country…"
            className="w-full pl-11 pr-4 py-3 rounded-2xl border text-base"
            style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
            data-testid="admin-campsites-search"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 no-scrollbar">
          <Filter className="w-4 h-4 opacity-60 shrink-0" />
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              data-testid={`admin-status-${t.key}`}
              className="whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-widest shrink-0 transition"
              style={{
                background: status === t.key ? "var(--brand)" : "color-mix(in srgb, var(--brand) 10%, transparent)",
                color: status === t.key ? "white" : "var(--brand)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}
      {!items && <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>}
      {items && items.length === 0 && (
        <div className="card-elev p-8 text-center" data-testid="admin-campsites-empty">
          <ShieldCheck className="w-8 h-8 mx-auto opacity-40" style={{ color: "var(--on-surface-muted)" }} />
          <div className="mt-2 text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>All clear</div>
          <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>No campsites match this filter.</div>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4" data-testid="admin-campsites-list">
          {items.map((c) => <Row key={c.campsite_id} c={c} busy={busyId === c.campsite_id}
                                 onVerify={() => doAction(c, "verify")}
                                 onReject={() => doAction(c, "reject")} />)}
        </div>
      )}
    </div>
  );
}

function Row({ c, busy, onVerify, onReject }) {
  const status = c.verification_status || "unverified";
  const statusMap = {
    pending_review: { fg: "var(--premium)", label: "Pending" },
    unverified:     { fg: "var(--on-surface-muted)", label: "Unverified" },
    verified:       { fg: "var(--brand)", label: "Verified" },
    rejected:       { fg: "#B45309", label: "Rejected" },
  };
  const style = statusMap[status] || statusMap.unverified;
  const canAct = status !== "verified";

  return (
    <article className="card-elev p-4 flex flex-col md:flex-row gap-4"
             style={{ opacity: busy ? 0.6 : 1 }} data-testid={`admin-row-${c.campsite_id}`}>
      <Link to={`/campsites/${c.campsite_id}`}
            className="w-full md:w-40 aspect-[16/10] rounded-xl bg-black/10 overflow-hidden shrink-0">
        {c.photo ? (
          <img src={c.photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--on-surface-muted)" }}>
            <MapPin className="w-6 h-6 opacity-40" />
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <Link to={`/campsites/${c.campsite_id}`} className="text-base font-extrabold leading-tight"
                style={{ color: "var(--on-surface)" }}>{c.name}</Link>
          <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ background: "color-mix(in srgb, currentColor 15%, transparent)", color: style.fg }}>
            {style.label}
          </span>
        </div>
        <div className="text-xs mt-1" style={{ color: "var(--on-surface-muted)" }}>
          <MapPin className="w-3 h-3 inline mr-1" />
          {[c.address, c.state, c.country || "Malaysia"].filter(Boolean).join(", ")}
        </div>
        {c.owner_name && (
          <div className="text-xs mt-1" style={{ color: "var(--on-surface-muted)" }}>
            Owner: <b>{c.owner_name}</b> · {c.owner_email}
          </div>
        )}
        {c.description && (
          <p className="text-xs mt-2 line-clamp-2" style={{ color: "var(--on-surface)" }}>
            {c.description}
          </p>
        )}
      </div>
      {canAct && (
        <div className="flex md:flex-col gap-2 shrink-0">
          {status !== "verified" && (
            <button onClick={onVerify} disabled={busy}
                    className="btn btn-green justify-center" data-testid={`admin-verify-${c.campsite_id}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Approve</>}
            </button>
          )}
          {status !== "rejected" && status !== "verified" && (
            <button onClick={onReject} disabled={busy}
                    className="btn btn-light justify-center" style={{ color: "#B45309" }}
                    data-testid={`admin-reject-${c.campsite_id}`}>
              <X className="w-4 h-4" /> Reject
            </button>
          )}
        </div>
      )}
    </article>
  );
}
